import {
  Avatar,
  Box,
  Card,
  CardContent,
  Divider,
  IconButton,
  Button,
  Paper,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Menu,
  MenuItem
} from '@mui/material';
import { toast } from 'react-toastify';
import Navbar from '../../global/Navbar';
import { shades } from '../../../../theme.js';
import { Add, Chat } from '@mui/icons-material';
import formatTime from '../utility/formatTime.js';
import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import FavoriteIcon from '@mui/icons-material/Favorite';
import SideBar from '../../../components/medichat/sidebar.jsx';
import Profile from '../../../components/medichat/profile.jsx';
import { findPostComments } from '../utility/findPostComments.js';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import { fetchPosts, createPost } from '../../../redux/middleware/medichat/feeds/posts.js';

import useWebSocket from '../../../redux/middleware/medichat/websocket/webSocketConnection.js';
import {
  createComment,
  fetchComments
} from '../../../redux/middleware/medichat/feeds/comments.js';

// Testing Data
import dummyPosts from '../../../../../dummy_data.js';
const c_user = dummyPosts[0].user;

// Utility function to convert a string to title case
function toTitleCase (str) {
  return str
    .toLowerCase()
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}
// Main Feed Section
const Feed = () => {
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedPost, setSelectedPost] = useState(null);
  const [comment, setComment] = useState('');
  const [postComments, setPostComments] = useState([]);
  const { sendReaction } = useWebSocket();
  const [lovedPosts, setLovedPosts] = useState(new Set());

  const { user } = useSelector((state) => state.patientAuth) || {};

  const { posts, loading } = useSelector((state) => state.posts) || [];
  const { comments } = useSelector((state) => state.comments) || [];
  const websocketMessage = useSelector(
    (state) => state.webSocket.websocketMessage
  );

  useEffect(() => {
    dispatch(fetchPosts());
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchComments());
  }, [dispatch]);

  const handleAddComment = () => {
    if (!comment.trim()) {
      toast.error('You accidentally submitted without typing a comment!');
      return;
    }
    if (!user) {
      toast.error('User not logged in');
      return;
    }
    // Create new comment using the actual user details
    const newComment = {
      id: Date.now(),
      author: {
        first_name: user.first_name,
        last_name: user.last_name || ''
      },
      content: comment,
      created_at: new Date().toISOString()
    };

    setPostComments((prevComments) => [newComment, ...prevComments]);

    try {
      dispatch(createComment(selectedPost.id, comment));
      setComment('');
    } catch (error) {
      toast.error(error.message || 'Error creating comment');
    }
  };

  useEffect(() => {
    if (websocketMessage?.type === 'reaction') {
      dispatch(fetchPosts());
    }
  }, [websocketMessage, dispatch]);

  const toggleLove = (postId) => {
    setLovedPosts((prevLovedPosts) => {
      const newLovedPosts = new Set(prevLovedPosts);
      if (newLovedPosts.has(postId)) {
        newLovedPosts.delete(postId);
      } else {
        newLovedPosts.add(postId);
      }
      return newLovedPosts;
    });
    handleLoveClick(postId);
  };

  const handleLoveClick = (postId) => {
    sendReaction(postId, 'LOVE');
  };

  // Open & Close Dialog for Creating Post
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  // Open & Close Menu for Post Options
  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // Handle Post Click
  const handlePostClick = (post) => {
    setSelectedPost(post);
    setPostComments(findPostComments(post.id, comments));
    if (!comments[post.id]) {
      dispatch(fetchComments(post.id));
    }
  };

  const handlePostPublish = async () => {
    if (!content || !title) {
      toast.error('You must provide a title and content');
      return;
    }
    try {
      dispatch(createPost({ title, content }));
      toast.success('Post created successfully');
      setOpen(false);
      setTitle('');
      setContent('');
    } catch (error) {
      toast.error(error.message || 'Error creating post');
    }
  };

  return (
    <Box
      sx={{
			  backgroundColor: shades.white[100],
			  flexGrow: 1,
			  p: 2,
			  marginTop: '10vh',
			  overflowY: 'auto',
			  maxHeight: 'calc(100vh - 10vh)',
			  maxWidth: { xs: '100%', sm: '100%', md: 'calc(100vw - 550px)' },
			  width: '100%'
      }}
    >
      <Typography
        variant='h6'
        sx={{ mb: 2, color: shades.neutral[900], fontWeight: 'bold' }}
      >
        Feed
      </Typography>
      {/* Start a post section */}
      <Paper
        sx={{
				  display: 'flex',
				  alignItems: 'center',
				  justifyContent: 'space-between',
				  p: 1.5,
				  mb: 2,
				  borderRadius: 10,
				  backgroundColor: shades.white[100],
				  boxShadow: 'none',
				  border: '1px solid #ddd'
        }}
      >
        <Avatar
          sx={{
					  width: 40,
					  height: 40,
					  mr: 1,
					  backgroundColor: shades.neutral[200],
					  color: shades.white[100]
          }}
        />
        {/* Placeholder Text */}
        <Typography
          sx={{
					  flexGrow: 1,
					  color: '#555',
					  fontSize: '1rem',
					  cursor: 'pointer',
					  pl: 1
          }}
          onClick={handleOpen}
        >
          Start a post...
        </Typography>
        <IconButton
          onClick={handleOpen}
          sx={{
					  color: shades.neutral[700],
					  backgroundColor: shades.white[100],
					  p: 1
          }}
        >
          <Add />
        </IconButton>
      </Paper>
      {/* Posts render */}
      {posts.map((post) => (
        <Card
          key={post.id}
          onClick={() => handlePostClick(post)}
          sx={{
					  mb: 2,
					  backgroundColor: shades.white[100],
					  boxShadow: 'none',
					  border: '1px solid #ddd',
					  cursor: 'pointer'
          }}
        >
          <CardContent
            sx={{
						  backgroundColor: shades.white[100],
						  borderRadius: 5
            }}
          >
            <Box
              display='flex'
              alignItems='center'
              justifyContent='space-between'
            >
              <Box display='flex' alignItems='center'>
                <Avatar
                  src='https://randomuser.me/api/portraits/men/32.jpg'
                  sx={{ mr: 2 }}
                />
                <Box>
                  <Typography
                    variant='subtitle1'
                    fontWeight='bold'
                    sx={{ color: shades.secondary[700] }}
                  >
                    {toTitleCase(
											`${post.author.first_name} ${post.author.last_name}`
                  )}
                  </Typography>

                  <Typography
                    variant='caption'
                    sx={{ color: shades.neutral[600] }}
                  >
                    {formatTime(post.created_at)}
                  </Typography>
                </Box>
              </Box>
              <IconButton
                sx={{ color: shades.neutral[700] }}
                onClick={(e) => {
								  e.stopPropagation();
								  handleMenuOpen(e);
                }}
              >
                <MoreVertIcon fontSize='small' />
              </IconButton>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
              >
                <MenuItem
                  onClick={handleMenuClose}
                  sx={{ backgroundColor: '#2e2e2e' }}
                >
                  Add Friend
                </MenuItem>
              </Menu>
            </Box>
            <Typography
              variant='h6'
              sx={{ mt: 2, color: shades.neutral[800], fontWeight: 'bold' }}
            >
              {post.title}
            </Typography>
            <Typography
              variant='body1'
              sx={{
							  mt: 1,
							  color: shades.secondary[300],
							  wordWrap: 'break-word',
							  whiteSpace: 'normal'
              }}
            >
              {post.content}
            </Typography>
            <Divider sx={{ my: 2, backgroundColor: shades.neutral[200] }} />
            <Box
              sx={{
							  display: 'flex',
							  mt: 2,
							  backgroundColor: shades.white[100],
							  display: 'flex',
							  justifyContent: 'flex-end'
              }}
            >
              <IconButton
                sx={{
								  color: lovedPosts.has(post.id) ? 'red' : shades.neutral[700],
								  fontSize: 'small'
                }}
                onClick={(e) => {
								  e.stopPropagation();
								  toggleLove(post.id);
                }}
              >
                {lovedPosts.has(post.id)
                  ? (
                    <FavoriteIcon fontSize='small' />
                    )
                  : (
                    <FavoriteBorderIcon fontSize='small' />
                    )}
                {post.reaction_count.love > 0 && post.reaction_count.love}
              </IconButton>
              <IconButton
                sx={{
								  color: shades.neutral[700]
                }}
                onClick={(e) => {
								  e.stopPropagation();
								  handlePostClick(post);
                }}
              >
                <Chat fontSize='small' />
              </IconButton>
            </Box>
          </CardContent>
        </Card>
      ))}
      {/* Create Post Dialog */}
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth='sm'
        PaperProps={{
				  sx: {
				    height: '100vh',
				    display: 'flex',
				    flexDirection: 'column'
          }
        }}
      >
        <DialogTitle
          sx={{
					  backgroundColor: shades.white[100],
					  p: 2,
					  display: 'flex',
					  alignItems: 'center',
					  gap: 2
          }}
        >
          <Avatar
            src='https://randomuser.me/api/portraits/men/32.jpg'
            sx={{
						  width: 40,
						  height: 40,
						  backgroundColor: shades.neutral[200],
						  color: shades.white[100]
            }}
          />
          <Typography variant='h6' sx={{ color: shades.neutral[700] }}>
            testUser
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: shades.white[100], p: 3 }}>
          <TextField
            placeholder='Title'
            fullWidth
            variant='standard'
            margin='dense'
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            InputProps={{
						  disableUnderline: false,
						  sx: {
						    fontSize: '1.2rem',
						    fontWeight: 400,
						    color: shades.neutral[800]
              }
            }}
          />
          <TextField
            placeholder="What's on your mind?"
            fullWidth
            variant='standard'
            multiline
            rows={4}
            margin='dense'
            value={content}
            onChange={(e) => setContent(e.target.value)}
            InputProps={{
						  disableUnderline: true,
						  sx: { fontSize: '1rem', color: shades.neutral[900] }
            }}
            sx={{
						  mt: 2,
						  p: 1,
						  borderRadius: 2,
						  backgroundColor: shades.neutral[100]
            }}
          />
        </DialogContent>
        <DialogActions sx={{ backgroundColor: shades.white[100], p: 2 }}>
          <Button onClick={handleClose} color='primary'>
            Cancel
          </Button>
          <Button
            onClick={handlePostPublish}
            variant='contained'
            disabled={!content}
            sx={{
						  backgroundColor: 'black',
						  '&:hover': {
						    backgroundColor: shades.secondary[400],
						    color: 'white'
              },
						  '&.Mui-disabled': {
						    backgroundColor: shades.secondary[200],
						    color: 'white'
              }
            }}
          >
            Publish
          </Button>
        </DialogActions>
      </Dialog>
      // Update the comment section part in the Dialog component // Replace the
      existing comment section code in your Feed component
      {/* Add comment dialogue */}
      {selectedPost && (
        <Dialog
          open={Boolean(selectedPost)}
          onClose={() => setSelectedPost(null)}
          fullWidth
          maxWidth='sm'
        >
          <DialogContent sx={{ backgroundColor: shades.white[100], p: 3 }}>
            {/* Post details section */}
            <Box display='flex' mb={3}>
              <Avatar
                src='https://randomuser.me/api/portraits/men/32.jpg'
                sx={{ mr: 2, width: 40, height: 40 }}
              />
              <Box>
                <Typography
                  variant='subtitle1'
                  fontWeight='bold'
                  sx={{ color: shades.secondary[700] }}
                >
                  {`${selectedPost.author.first_name} ${selectedPost.author.last_name}`}
                </Typography>
                <Typography
                  variant='caption'
                  sx={{ color: shades.neutral[500] }}
                >
                  {formatTime(selectedPost.created_at)}
                </Typography>
                <Typography
                  variant='h6'
                  sx={{ mt: 1, color: shades.neutral[800], fontWeight: 'bold' }}
                >
                  {selectedPost.title}
                </Typography>
                <Typography
                  variant='body1'
                  sx={{ color: shades.neutral[800], my: 1 }}
                >
                  {selectedPost.content}
                </Typography>
              </Box>
            </Box>

            {/* Reaction indicators */}
            <Box
              sx={{
							  display: 'flex',
							  justifyContent: 'flex-start',
							  alignItems: 'center',
							  mb: 2
              }}
            >
              <Box
                sx={{
								  display: 'flex',
								  alignItems: 'center',
								  color: shades.neutral[600],
								  mr: 2,
								  fontSize: '0.875rem'
                }}
              >
                <FavoriteIcon fontSize='small' sx={{ color: 'red', mr: 0.5 }} />
                {selectedPost.reaction_count?.love || 0} likes
              </Box>
              <Box
                sx={{
								  display: 'flex',
								  alignItems: 'center',
								  color: shades.neutral[600],
								  fontSize: '0.875rem'
                }}
              >
                <Chat fontSize='small' sx={{ mr: 0.5 }} />
                {postComments.length} comments
              </Box>
            </Box>

            <Divider sx={{ mb: 3 }} />

            {/* Comments section */}
            <Typography
              variant='h6'
              sx={{ color: shades.neutral[700], mb: 2, fontWeight: 'medium' }}
            >
              Comments
            </Typography>

            {/* Comment list */}
            <Box sx={{ maxHeight: '300px', overflowY: 'auto', mb: 3 }}>
              {postComments.length > 0
                ? (
							  postComments.map((comment) => (
  <Box key={comment.id} sx={{ mb: 2 }}>
    <Box display='flex'>
      <Avatar
        sx={{
												  width: 32,
												  height: 32,
												  mr: 1.5,
												  backgroundColor: shades.neutral[200],
												  color: shades.white[100]
        }}
      />
      <Box
        sx={{
												  backgroundColor: shades.neutral[100],
												  borderRadius: 2,
												  p: 1.5,
												  flex: 1
        }}
      >
        <Typography
          variant='subtitle2'
          fontWeight='bold'
          sx={{ color: shades.neutral[800] }}
        >
          {comment.author.first_name}{' '}
          {comment.author.last_name || ''}
        </Typography>
        <Typography
          variant='body2'
          sx={{ color: shades.neutral[700], mt: 0.5 }}
        >
          {comment.content}
        </Typography>
        <Typography
          variant='caption'
          sx={{
													  color: shades.neutral[500],
													  mt: 0.5,
													  display: 'block'
          }}
        >
          {comment.created_at
													  ? formatTime(comment.created_at)
													  : 'Just now'}
        </Typography>
      </Box>
    </Box>
  </Box>
							  ))
                  )
                : (
                  <Typography
                    variant='body2'
                    sx={{
									  color: shades.neutral[500],
									  textAlign: 'center',
									  py: 2
                  }}
                  >
    No comments yet. Be the first to comment!
                  </Typography>
                  )}
            </Box>

            {/* Add comment section */}
            <Divider sx={{ mb: 2 }} />
            <Box
              sx={{
							  display: 'flex',
							  alignItems: 'flex-start',
							  mb: 1
              }}
            >
              <Avatar
                sx={{
								  width: 32,
								  height: 32,
								  mr: 1.5
                }}
              />
              <TextField
                placeholder='Write a comment...'
                fullWidth
                variant='outlined'
                multiline
                minRows={1}
                maxRows={4}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                sx={{
								  '& .MuiOutlinedInput-root': {
								    borderRadius: 3,
								    backgroundColor: shades.neutral[100],
								    '& fieldset': {
								      borderColor: 'transparent'
                    },
								    '&:hover fieldset': {
								      borderColor: shades.neutral[300]
                    },
								    '&.Mui-focused fieldset': {
								      borderColor: shades.neutral[400]
                    }
								  },
								  '& .MuiInputBase-input': {
								    color: shades.neutral[800] // Explicitly setting text color to ensure visibility
                  },
								  '& .MuiOutlinedInput-inputMultiline': {
								    color: shades.neutral[800] // Making sure multiline input is also properly colored
                  }
                }}
                InputProps={{
								  style: { color: shades.neutral[800] }, // Additional explicit color setting
								  endAdornment: (
  <Button
    disabled={!comment.trim()}
    onClick={handleAddComment}
    sx={{
											  minWidth: 'auto',
											  backgroundColor: comment.trim()
											    ? shades.secondary[700]
											    : 'transparent',
											  color: comment.trim()
											    ? shades.white[100]
											    : shades.neutral[400],
											  borderRadius: 2,
											  '&:hover': {
											    backgroundColor: shades.secondary[800]
      },
											  '&.Mui-disabled': {
											    color: shades.neutral[400],
											    backgroundColor: 'transparent'
      }
    }}
  >
    Post
  </Button>
								  )
                }}
              />
            </Box>
          </DialogContent>
          <DialogActions
            sx={{ backgroundColor: shades.white[100], px: 3, py: 2 }}
          >
            <Button
              onClick={() => setSelectedPost(null)}
              sx={{
							  color: shades.neutral[700]
              }}
            >
              Close
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </Box>
  );
};

const HomeFeeds = () => {
  return (
    <Box sx={{ display: 'flex', height: '100vh' }}>
      <Navbar />
      <SideBar />
      <Feed />
      <Profile />
    </Box>
  );
};

export default HomeFeeds;
